import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { PageHeader, SectionCard } from "@/components/kit";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { fmtDate } from "@/lib/helpers";
import { openInvoicePrint } from "@/lib/invoiceTemplate";
import { Plus, Printer, Pencil, Trash2, Ship, FileSpreadsheet } from "lucide-react";
import { toast } from "sonner";

const CURRENCIES = ["USD", "AUD", "INR", "EUR", "GBP", "AED", "SGD"];
const today = () => new Date().toISOString().slice(0, 10);

const emptyInvoice = () => ({
  date: today(), currency: "USD",
  exporter_name: "VNS Vinayaka Group of Enterprises",
  exporter_address: "Hyderabad, Telangana, India · Tel +91 7013675041, +91 7013797110",
  exporter_gstin: "36GJUKA7840C2K8", exporter_iec: "BTRPS6842M", exporter_pan: "FJQPS7284K",
  consignee_name: "", consignee_address: "", consignee_ref: "",
  order_no: "", order_date: "", payment_terms: "100% against documents", delivery_terms: "CIF (Incoterms 2020)",
  pre_carriage: "", vessel: "", mode: "Sea Freight (FCL)", port_loading: "Nhava Sheva (JNPT), India",
  port_discharge: "", final_destination: "", container_seal: "", marks: "", country_origin: "India",
  items: [{ description: "", hs_code: "", qty: "", rate: 0, amount: 0 }],
  freight: 0, insurance: 0, packing: "", net_weight: "", gross_weight: "",
  bank_beneficiary: "VNS Vinayaka Group of Enterprises", bank_name: "", bank_account: "", bank_ifsc: "", bank_swift: "", bank_ad_code: "",
  notes: "",
});

export default function Invoices() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyInvoice());

  const { data: invoices = [], isLoading } = useQuery({ queryKey: ["invoices"], queryFn: async () => (await api.get("/invoices")).data });
  const { data: nextNum } = useQuery({ queryKey: ["invoice-next"], queryFn: async () => (await api.get("/invoices/next-number")).data, enabled: open && !editing });

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setItem = (i, k, v) => setForm((f) => {
    const items = f.items.map((it, idx) => {
      if (idx !== i) return it;
      const next = { ...it, [k]: v };
      if (k === "rate" || k === "qty") {
        const q = parseFloat(next.qty), r = parseFloat(next.rate);
        if (!isNaN(q) && !isNaN(r)) next.amount = Math.round(q * r * 100) / 100;
      }
      return next;
    });
    return { ...f, items };
  });
  const addItem = () => setForm((f) => ({ ...f, items: [...f.items, { description: "", hs_code: "", qty: "", rate: 0, amount: 0 }] }));
  const delItem = (i) => setForm((f) => ({ ...f, items: f.items.filter((_, idx) => idx !== i) }));

  const fob = form.items.reduce((s, i) => s + Number(i.amount || 0), 0);
  const total = fob + Number(form.freight || 0) + Number(form.insurance || 0);

  const save = useMutation({
    mutationFn: async () => {
      const payload = { ...form, freight: Number(form.freight || 0), insurance: Number(form.insurance || 0), items: form.items.map((i) => ({ ...i, rate: Number(i.rate || 0), amount: Number(i.amount || 0) })) };
      return editing ? api.put(`/invoices/${editing}`, payload) : api.post("/invoices", payload);
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["invoices"] }); toast.success(editing ? "Invoice updated" : "Invoice created"); setOpen(false); },
    onError: () => toast.error("Save failed"),
  });
  const del = useMutation({ mutationFn: (id) => api.delete(`/invoices/${id}`), onSuccess: () => { qc.invalidateQueries({ queryKey: ["invoices"] }); toast.success("Invoice deleted"); } });

  const openNew = () => { setEditing(null); setForm(emptyInvoice()); setOpen(true); };
  const openEdit = (inv) => { setEditing(inv.invoice_id); setForm({ ...emptyInvoice(), ...inv }); setOpen(true); };

  return (
    <div data-testid="invoices-page">
      <PageHeader title="Invoice Generator" subtitle="Create export invoices — Original, Duplicate & Triplicate copies">
        <Button asChild variant="outline" data-testid="staff-guide-btn" className="rounded-lg border-navy/20 text-navy hover:bg-navy hover:text-white">
          <a href="/VNS_Sample_Invoice_Guide.xlsx" download><FileSpreadsheet className="mr-2 h-4 w-4" /> Staff Guide (Excel)</a>
        </Button>
        <Button onClick={openNew} data-testid="new-invoice-btn" className="bg-navy hover:bg-navy-900 text-white rounded-lg"><Plus className="mr-2 h-4 w-4" /> New Invoice</Button>
      </PageHeader>

      <SectionCard testId="invoices-table-card">
        {isLoading ? <Skeleton className="h-64" /> : invoices.length === 0 ? (
          <div className="py-14 text-center text-slate-400">
            <Ship className="mx-auto mb-2 h-10 w-10 text-gold" />
            No invoices yet. Click <b>New Invoice</b> to create your first one.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader><TableRow>{["Invoice No.", "Date", "Consignee", "Currency", "Total", "Created By", ""].map((h) => <TableHead key={h} className="text-navy font-semibold">{h}</TableHead>)}</TableRow></TableHeader>
              <TableBody>
                {invoices.map((inv) => (
                  <TableRow key={inv.invoice_id} data-testid={`invoice-row-${inv.invoice_id}`} className="hover:bg-slate-50">
                    <TableCell className="font-medium text-navy">{inv.number}</TableCell>
                    <TableCell>{fmtDate(inv.date)}</TableCell>
                    <TableCell className="max-w-[220px] truncate">{inv.consignee_name || "—"}</TableCell>
                    <TableCell>{inv.currency}</TableCell>
                    <TableCell className="font-semibold">{inv.currency} {Number(inv.total || 0).toLocaleString("en-US", { minimumFractionDigits: 2 })}</TableCell>
                    <TableCell className="text-slate-500">{inv.created_by}</TableCell>
                    <TableCell className="text-right whitespace-nowrap">
                      <Button variant="ghost" size="icon" title="View / Print" onClick={() => openInvoicePrint(inv)} data-testid={`print-invoice-${inv.invoice_id}`}><Printer className="h-4 w-4 text-navy" /></Button>
                      <Button variant="ghost" size="icon" title="Edit" onClick={() => openEdit(inv)} data-testid={`edit-invoice-${inv.invoice_id}`}><Pencil className="h-4 w-4 text-navy" /></Button>
                      <Button variant="ghost" size="icon" title="Delete" onClick={() => { if (window.confirm(`Delete invoice ${inv.number}? This cannot be undone.`)) del.mutate(inv.invoice_id); }} data-testid={`delete-invoice-${inv.invoice_id}`}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </SectionCard>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading text-navy">{editing ? "Edit Invoice" : `New Invoice${nextNum ? " · " + nextNum.number : ""}`}</DialogTitle>
          <DialogDescription>Fill in the shipment and goods details, then save to generate the Original, Duplicate &amp; Triplicate copies.</DialogDescription></DialogHeader>

          <div className="space-y-5">
            <div className="grid grid-cols-3 gap-3">
              <div><Label>Invoice Date</Label><Input type="date" data-testid="inv-date" value={form.date} onChange={(e) => set("date", e.target.value)} /></div>
              <div>
                <Label>Currency</Label>
                <Select value={form.currency} onValueChange={(v) => set("currency", v)}>
                  <SelectTrigger data-testid="inv-currency"><SelectValue /></SelectTrigger>
                  <SelectContent>{CURRENCIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Country of Origin</Label><Input value={form.country_origin} onChange={(e) => set("country_origin", e.target.value)} /></div>
            </div>

            <fieldset className="rounded-lg border border-slate-200 p-3">
              <legend className="px-1 text-xs font-semibold uppercase text-slate-500">Exporter (Seller)</legend>
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2"><Label>Name</Label><Input value={form.exporter_name} onChange={(e) => set("exporter_name", e.target.value)} /></div>
                <div className="col-span-2"><Label>Address</Label><Input value={form.exporter_address} onChange={(e) => set("exporter_address", e.target.value)} /></div>
                <div><Label>GSTIN</Label><Input value={form.exporter_gstin} onChange={(e) => set("exporter_gstin", e.target.value)} /></div>
                <div><Label>IEC</Label><Input value={form.exporter_iec} onChange={(e) => set("exporter_iec", e.target.value)} /></div>
                <div><Label>PAN</Label><Input value={form.exporter_pan} onChange={(e) => set("exporter_pan", e.target.value)} /></div>
              </div>
            </fieldset>

            <fieldset className="rounded-lg border border-slate-200 p-3">
              <legend className="px-1 text-xs font-semibold uppercase text-slate-500">Consignee (Buyer)</legend>
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2"><Label>Name</Label><Input data-testid="inv-consignee" value={form.consignee_name} onChange={(e) => set("consignee_name", e.target.value)} /></div>
                <div className="col-span-2"><Label>Address</Label><Textarea rows={2} value={form.consignee_address} onChange={(e) => set("consignee_address", e.target.value)} /></div>
                <div><Label>Reference (ABN / Tax ID)</Label><Input value={form.consignee_ref} onChange={(e) => set("consignee_ref", e.target.value)} /></div>
                <div><Label>Buyer's Order No.</Label><Input value={form.order_no} onChange={(e) => set("order_no", e.target.value)} /></div>
              </div>
            </fieldset>

            <fieldset className="rounded-lg border border-slate-200 p-3">
              <legend className="px-1 text-xs font-semibold uppercase text-slate-500">Shipment</legend>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Payment Terms</Label><Input value={form.payment_terms} onChange={(e) => set("payment_terms", e.target.value)} /></div>
                <div><Label>Delivery Terms</Label><Input value={form.delivery_terms} onChange={(e) => set("delivery_terms", e.target.value)} /></div>
                <div><Label>Pre-Carriage / Place of Receipt</Label><Input value={form.pre_carriage} onChange={(e) => set("pre_carriage", e.target.value)} /></div>
                <div><Label>Vessel / Voyage No.</Label><Input value={form.vessel} onChange={(e) => set("vessel", e.target.value)} /></div>
                <div><Label>Port of Loading</Label><Input value={form.port_loading} onChange={(e) => set("port_loading", e.target.value)} /></div>
                <div><Label>Port of Discharge</Label><Input value={form.port_discharge} onChange={(e) => set("port_discharge", e.target.value)} /></div>
                <div><Label>Final Destination</Label><Input value={form.final_destination} onChange={(e) => set("final_destination", e.target.value)} /></div>
                <div><Label>Container / Seal No.</Label><Input value={form.container_seal} onChange={(e) => set("container_seal", e.target.value)} /></div>
                <div><Label>Marks &amp; Nos.</Label><Input value={form.marks} onChange={(e) => set("marks", e.target.value)} /></div>
                <div><Label>Mode</Label><Input value={form.mode} onChange={(e) => set("mode", e.target.value)} /></div>
              </div>
            </fieldset>

            <fieldset className="rounded-lg border border-slate-200 p-3">
              <legend className="px-1 text-xs font-semibold uppercase text-slate-500">Goods</legend>
              <div className="space-y-2">
                {form.items.map((it, i) => (
                  <div key={i} className="grid grid-cols-12 gap-2 items-end" data-testid={`inv-item-${i}`}>
                    <div className="col-span-4"><Label className="text-xs">Description</Label><Input value={it.description} onChange={(e) => setItem(i, "description", e.target.value)} /></div>
                    <div className="col-span-1"><Label className="text-xs">HS Code</Label><Input value={it.hs_code} onChange={(e) => setItem(i, "hs_code", e.target.value)} /></div>
                    <div className="col-span-2"><Label className="text-xs">Qty</Label><Input value={it.qty} onChange={(e) => setItem(i, "qty", e.target.value)} placeholder="500" /></div>
                    <div className="col-span-2"><Label className="text-xs">Rate</Label><Input type="number" value={it.rate} onChange={(e) => setItem(i, "rate", e.target.value)} /></div>
                    <div className="col-span-2"><Label className="text-xs">Amount</Label><Input type="number" value={it.amount} onChange={(e) => setItem(i, "amount", e.target.value)} /></div>
                    <div className="col-span-1"><Button variant="ghost" size="icon" onClick={() => delItem(i)}><Trash2 className="h-4 w-4 text-red-500" /></Button></div>
                  </div>
                ))}
                <Button variant="outline" size="sm" onClick={addItem} data-testid="add-item-btn" className="rounded-lg"><Plus className="mr-1 h-3.5 w-3.5" /> Add Item</Button>
              </div>
              <div className="mt-3 grid grid-cols-3 gap-3">
                <div><Label>Freight ({form.currency})</Label><Input type="number" value={form.freight} onChange={(e) => set("freight", e.target.value)} /></div>
                <div><Label>Insurance ({form.currency})</Label><Input type="number" value={form.insurance} onChange={(e) => set("insurance", e.target.value)} /></div>
                <div className="flex flex-col justify-end">
                  <span className="text-xs text-slate-500">FOB {form.currency} {fob.toLocaleString("en-US", { minimumFractionDigits: 2 })}</span>
                  <span className="font-heading text-lg font-bold text-navy">Total: {form.currency} {total.toLocaleString("en-US", { minimumFractionDigits: 2 })}</span>
                </div>
              </div>
              <div className="mt-3 grid grid-cols-3 gap-3">
                <div><Label>Packing</Label><Input value={form.packing} onChange={(e) => set("packing", e.target.value)} placeholder="40 Cartons" /></div>
                <div><Label>Net Weight</Label><Input value={form.net_weight} onChange={(e) => set("net_weight", e.target.value)} placeholder="1,850 kg" /></div>
                <div><Label>Gross Weight</Label><Input value={form.gross_weight} onChange={(e) => set("gross_weight", e.target.value)} placeholder="2,050 kg" /></div>
              </div>
            </fieldset>

            <fieldset className="rounded-lg border border-slate-200 p-3">
              <legend className="px-1 text-xs font-semibold uppercase text-slate-500">Bank Details</legend>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Beneficiary</Label><Input value={form.bank_beneficiary} onChange={(e) => set("bank_beneficiary", e.target.value)} /></div>
                <div><Label>Bank &amp; Branch</Label><Input value={form.bank_name} onChange={(e) => set("bank_name", e.target.value)} /></div>
                <div><Label>Account No.</Label><Input value={form.bank_account} onChange={(e) => set("bank_account", e.target.value)} /></div>
                <div><Label>IFSC</Label><Input value={form.bank_ifsc} onChange={(e) => set("bank_ifsc", e.target.value)} /></div>
                <div><Label>SWIFT</Label><Input value={form.bank_swift} onChange={(e) => set("bank_swift", e.target.value)} /></div>
                <div><Label>AD Code</Label><Input value={form.bank_ad_code} onChange={(e) => set("bank_ad_code", e.target.value)} /></div>
              </div>
            </fieldset>

            <div><Label>Notes</Label><Input value={form.notes} onChange={(e) => set("notes", e.target.value)} /></div>
          </div>

          <DialogFooter>
            <Button onClick={() => save.mutate()} disabled={save.isPending} data-testid="save-invoice-btn" className="bg-navy text-white">{save.isPending ? "Saving…" : editing ? "Update Invoice" : "Create Invoice"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
